import { useEffect, useRef, useState, type ReactNode } from 'react'
import { CheckIcon, PlusIcon, TrashIcon, XIcon } from 'lucide-react'
import { Button } from '@/components/ui/shadcn/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/shadcn/popover'
import type { MoodboardBriefBlock } from '@backend/lib/db/schema/moodboard'

// Brief block renderer + editor.
//
// Each block is editable in place: text blocks click-to-edit, list blocks have inline
// add / remove affordances, the palette swatch opens a color picker.
//
// Block-level deletion is delegated to the parent via `onDelete`; the renderer wraps
// each block in a hover container that surfaces a small trash affordance.
//
// State model: each editable surface keeps a local `draft` while focused, then calls
// `onChange(nextBlock)` on commit (blur / Enter / picker close). The parent persists
// the whole brief.blocks array via tRPC update.

type Props<B extends MoodboardBriefBlock = MoodboardBriefBlock> = {
  block: B
  onChange: (next: B) => void
  onDelete: () => void
}

export function BlockRenderer({ block, onChange, onDelete }: Props) {
  return (
    <BlockShell onDelete={onDelete}>
      <BlockBody block={block} onChange={onChange} />
    </BlockShell>
  )
}

function BlockBody({ block, onChange }: Pick<Props, 'block' | 'onChange'>) {
  switch (block.type) {
    case 'throughline':
      return (
        <ThroughlineBlock block={block} onChange={onChange as Props<typeof block>['onChange']} />
      )
    case 'palette':
      return <PaletteBlock block={block} onChange={onChange as Props<typeof block>['onChange']} />
    case 'audiences':
      return <AudiencesBlock block={block} onChange={onChange as Props<typeof block>['onChange']} />
    case 'keyPhrases':
      return (
        <KeyPhrasesBlock block={block} onChange={onChange as Props<typeof block>['onChange']} />
      )
    case 'watchFor':
      return <WatchForBlock block={block} onChange={onChange as Props<typeof block>['onChange']} />
    case 'notes':
      return <NotesBlock block={block} onChange={onChange as Props<typeof block>['onChange']} />
    case 'prose':
      return <ProseBlock block={block} onChange={onChange as Props<typeof block>['onChange']} />
    case 'bullets':
      return <BulletsBlock block={block} onChange={onChange as Props<typeof block>['onChange']} />
  }
}

// =========================================================================
// Shared primitives
// =========================================================================

function BlockShell({ children, onDelete }: { children: ReactNode; onDelete: () => void }) {
  return (
    <section className="group relative">
      <button
        type="button"
        onClick={onDelete}
        className="bg-background/80 text-muted-foreground hover:text-destructive absolute -right-1 -top-1 z-10 rounded-full p-1 opacity-0 shadow-sm transition-opacity group-hover:opacity-100"
        aria-label="Delete section">
        <TrashIcon size={11} />
      </button>
      {children}
    </section>
  )
}

function SectionLabel({ value, onChange }: { value: string; onChange?: (next: string) => void }) {
  if (!onChange) {
    return (
      <p className="text-muted-foreground text-[10px] font-medium uppercase tracking-[0.14em]">
        {value}
      </p>
    )
  }
  return (
    <InlineEditableText
      value={value}
      onChange={onChange}
      className="text-muted-foreground text-[10px] font-medium uppercase tracking-[0.14em]"
      placeholder="Section label"
    />
  )
}

// Single-line editable text. Click toggles to an input; blur or Enter commits.
function InlineEditableText({
  value,
  onChange,
  className,
  placeholder,
}: {
  value: string
  onChange: (next: string) => void
  className?: string
  placeholder?: string
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (editing) inputRef.current?.focus()
  }, [editing])

  const commit = () => {
    setEditing(false)
    const next = draft.trim()
    if (next && next !== value) onChange(next)
    else setDraft(value)
  }

  if (!editing) {
    return (
      <span
        className={`hover:bg-hover -mx-0.5 cursor-text rounded-sm px-0.5 transition-colors ${className ?? ''}`}
        onClick={() => {
          setDraft(value)
          setEditing(true)
        }}>
        {value || <span className="text-muted-foreground italic">{placeholder ?? '…'}</span>}
      </span>
    )
  }
  return (
    <input
      ref={inputRef}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault()
          commit()
        }
        if (e.key === 'Escape') {
          setDraft(value)
          setEditing(false)
        }
      }}
      className={`focus:ring-primary/40 -mx-0.5 w-full rounded-sm bg-transparent px-0.5 outline-none focus:ring-1 ${className ?? ''}`}
      placeholder={placeholder}
    />
  )
}

// Multi-line editable text. Auto-sizes to its content so the textarea never crops or
// leaves a big empty void below the cursor. Reset-then-measure pattern lets it shrink
// when the user deletes content.
function InlineEditableTextarea({
  value,
  onChange,
  className,
  placeholder,
}: {
  value: string
  onChange: (next: string) => void
  className?: string
  placeholder?: string
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)
  const taRef = useRef<HTMLTextAreaElement>(null)

  const autoSize = () => {
    const el = taRef.current
    if (!el) return
    // Reset before measuring so the textarea can shrink, not just grow.
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }

  useEffect(() => {
    if (editing) {
      taRef.current?.focus()
      autoSize()
    }
  }, [editing])

  const commit = () => {
    setEditing(false)
    const next = draft.trim()
    if (next !== value) onChange(next)
  }

  if (!editing) {
    return (
      <p
        className={`hover:bg-hover -mx-0.5 cursor-text whitespace-pre-wrap rounded-sm px-0.5 transition-colors ${className ?? ''}`}
        onClick={() => {
          setDraft(value)
          setEditing(true)
        }}>
        {value || (
          <span className="text-muted-foreground italic">{placeholder ?? 'Click to edit'}</span>
        )}
      </p>
    )
  }
  return (
    <textarea
      ref={taRef}
      value={draft}
      onChange={(e) => {
        setDraft(e.target.value)
        autoSize()
      }}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          setDraft(value)
          setEditing(false)
        }
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) commit()
      }}
      rows={1}
      className={`focus:ring-primary/40 -mx-0.5 w-full resize-none overflow-hidden rounded-sm bg-transparent px-0.5 outline-none focus:ring-1 ${className ?? ''}`}
      placeholder={placeholder}
    />
  )
}

// Inline input for adding new items to a list. Type + Enter to add; blur cancels.
function AddItemInput({
  onAdd,
  placeholder,
  className,
}: {
  onAdd: (value: string) => void
  placeholder: string
  className?: string
}) {
  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (adding) inputRef.current?.focus()
  }, [adding])

  if (!adding) {
    return (
      <button
        type="button"
        onClick={() => {
          setDraft('')
          setAdding(true)
        }}
        className={`text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-xs ${className ?? ''}`}>
        <PlusIcon size={12} />
        Add
      </button>
    )
  }
  const commit = () => {
    const next = draft.trim()
    if (next) onAdd(next)
    setDraft('')
    setAdding(false)
  }
  return (
    <input
      ref={inputRef}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault()
          commit()
        }
        if (e.key === 'Escape') {
          setDraft('')
          setAdding(false)
        }
      }}
      placeholder={placeholder}
      className={`focus:ring-primary/40 rounded-sm bg-transparent px-1 outline-none focus:ring-1 ${className ?? ''}`}
    />
  )
}

// =========================================================================
// Block implementations
// =========================================================================

type BlockOf<T extends MoodboardBriefBlock['type']> = Extract<MoodboardBriefBlock, { type: T }>

function ThroughlineBlock({
  block,
  onChange,
}: {
  block: BlockOf<'throughline'>
  onChange: (next: BlockOf<'throughline'>) => void
}) {
  return (
    <div className="space-y-2">
      <SectionLabel value="The throughline" />
      <div className="bg-accent/10 border-accent/30 rounded-r-md border-l-2 py-3 pl-4 pr-3">
        <InlineEditableTextarea
          value={block.text}
          onChange={(text) => onChange({ ...block, text })}
          className="text-foreground text-sm leading-snug"
          placeholder="One sentence capturing the brand's essence"
        />
      </div>
    </div>
  )
}

function PaletteBlock({
  block,
  onChange,
}: {
  block: BlockOf<'palette'>
  onChange: (next: BlockOf<'palette'>) => void
}) {
  const update = (i: number, patch: Partial<BlockOf<'palette'>['swatches'][number]>) => {
    const next = block.swatches.map((s, idx) => (idx === i ? { ...s, ...patch } : s))
    onChange({ ...block, swatches: next })
  }
  const remove = (i: number) => {
    onChange({ ...block, swatches: block.swatches.filter((_, idx) => idx !== i) })
  }
  const add = () => {
    onChange({
      ...block,
      swatches: [...block.swatches, { hex: '#888888', name: null, role: null }],
    })
  }
  return (
    <div className="space-y-2">
      <SectionLabel value="Palette" />
      <div className="grid grid-cols-2 gap-x-3 gap-y-3">
        {block.swatches.map((s, i) => (
          <div key={i} className="group/swatch flex items-start gap-2">
            <Popover>
              <PopoverTrigger asChild>
                <button
                  className="ring-border/60 mt-0.5 h-6 w-6 shrink-0 cursor-pointer rounded-sm ring-1 hover:ring-2"
                  style={{ backgroundColor: s.hex }}
                  aria-label={`Edit color ${s.hex}`}
                />
              </PopoverTrigger>
              <PopoverContent className="w-auto p-2">
                <ColorPickerInput value={s.hex} onChange={(hex) => update(i, { hex })} />
              </PopoverContent>
            </Popover>
            <div className="min-w-0 flex-1">
              <InlineEditableText
                value={s.name ?? ''}
                onChange={(name) => update(i, { name: name || null })}
                className="text-foreground block truncate text-xs font-medium"
                placeholder="Color name"
              />
              <p className="text-muted-foreground text-[11px]">{s.hex}</p>
              <InlineEditableTextarea
                value={s.role ?? ''}
                onChange={(role) => update(i, { role: role || null })}
                className="text-muted-foreground/80 mt-0.5 block text-[11px] leading-snug"
                placeholder="What it's for"
              />
            </div>
            <button
              type="button"
              onClick={() => remove(i)}
              className="text-muted-foreground hover:text-destructive shrink-0 opacity-0 transition-opacity group-hover/swatch:opacity-100"
              aria-label="Remove color">
              <XIcon size={11} />
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={add}
        className="text-muted-foreground hover:text-foreground mt-1 inline-flex items-center gap-1 text-xs">
        <PlusIcon size={12} />
        Add color
      </button>
    </div>
  )
}

function ColorPickerInput({ value, onChange }: { value: string; onChange: (hex: string) => void }) {
  const [draft, setDraft] = useState(value)
  return (
    <div className="space-y-2">
      <input
        type="color"
        value={normalizeHex(draft)}
        onChange={(e) => {
          setDraft(e.target.value)
          onChange(e.target.value)
        }}
        className="h-10 w-10 cursor-pointer rounded-sm border-none bg-transparent p-0"
      />
      <div className="flex items-center gap-1.5">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => {
            if (/^#[0-9a-fA-F]{6}$/.test(draft)) onChange(draft)
            else setDraft(value)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && /^#[0-9a-fA-F]{6}$/.test(draft)) onChange(draft)
          }}
          className="border-border focus:border-primary w-24 rounded-sm border px-1.5 py-0.5 font-mono text-xs outline-none"
          placeholder="#000000"
        />
        <CheckIcon size={12} className="text-muted-foreground" />
      </div>
    </div>
  )
}

function normalizeHex(hex: string): string {
  // <input type="color"> only accepts #rrggbb. Pad shorter / invalid input.
  if (/^#[0-9a-fA-F]{6}$/.test(hex)) return hex
  if (/^#[0-9a-fA-F]{3}$/.test(hex)) {
    return (
      '#' +
      hex
        .slice(1)
        .split('')
        .map((c) => c + c)
        .join('')
    )
  }
  return '#888888'
}

function AudiencesBlock({
  block,
  onChange,
}: {
  block: BlockOf<'audiences'>
  onChange: (next: BlockOf<'audiences'>) => void
}) {
  const update = (i: number, patch: Partial<BlockOf<'audiences'>['cards'][number]>) => {
    const next = block.cards.map((c, idx) => (idx === i ? { ...c, ...patch } : c))
    onChange({ ...block, cards: next })
  }
  const remove = (i: number) => {
    onChange({ ...block, cards: block.cards.filter((_, idx) => idx !== i) })
  }
  const add = () => {
    onChange({ ...block, cards: [...block.cards, { title: 'New audience', description: '' }] })
  }
  return (
    <div className="space-y-2">
      <SectionLabel value="Audiences" />
      <div className="space-y-2">
        {block.cards.map((c, i) => (
          <div
            key={i}
            className="group/card border-border/60 bg-background relative rounded-md border p-3">
            <InlineEditableText
              value={c.title}
              onChange={(title) => update(i, { title })}
              className="text-foreground block text-xs font-semibold"
              placeholder="Audience name"
            />
            <InlineEditableTextarea
              value={c.description}
              onChange={(description) => update(i, { description })}
              className="text-muted-foreground mt-1 text-[11px] leading-relaxed"
              placeholder="Who they are, what they want"
            />
            <button
              type="button"
              onClick={() => remove(i)}
              className="text-muted-foreground hover:text-destructive absolute right-1.5 top-1.5 opacity-0 transition-opacity group-hover/card:opacity-100"
              aria-label="Remove audience">
              <XIcon size={11} />
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={add}
        className="text-muted-foreground hover:text-foreground mt-1 inline-flex items-center gap-1 text-xs">
        <PlusIcon size={12} />
        Add audience
      </button>
    </div>
  )
}

function KeyPhrasesBlock({
  block,
  onChange,
}: {
  block: BlockOf<'keyPhrases'>
  onChange: (next: BlockOf<'keyPhrases'>) => void
}) {
  const update = (i: number, value: string) => {
    onChange({ ...block, phrases: block.phrases.map((p, idx) => (idx === i ? value : p)) })
  }
  const remove = (i: number) => {
    onChange({ ...block, phrases: block.phrases.filter((_, idx) => idx !== i) })
  }
  const add = (value: string) => {
    onChange({ ...block, phrases: [...block.phrases, value] })
  }
  return (
    <div className="space-y-2">
      <SectionLabel value="Key phrases" />
      <div className="flex flex-wrap items-center gap-1.5">
        {block.phrases.map((p, i) => (
          <span
            key={i}
            className="group/pill bg-muted text-foreground/90 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px]">
            <InlineEditableText
              value={p}
              onChange={(next) => update(i, next)}
              // Pills grow with their content via `field-sizing: content`. `w-auto`
              // unsets the default `w-full` we apply to inputs so the pill width
              // tracks the typed text.
              className="text-foreground/90 w-auto min-w-[3rem] text-[11px] [field-sizing:content]"
              placeholder="phrase"
            />
            <button
              type="button"
              onClick={() => remove(i)}
              className="text-muted-foreground hover:text-destructive opacity-0 transition-opacity group-hover/pill:opacity-100"
              aria-label="Remove phrase">
              <XIcon size={10} />
            </button>
          </span>
        ))}
        <AddItemInput onAdd={add} placeholder="phrase + Enter" className="text-[11px]" />
      </div>
    </div>
  )
}

function WatchForBlock({
  block,
  onChange,
}: {
  block: BlockOf<'watchFor'>
  onChange: (next: BlockOf<'watchFor'>) => void
}) {
  const update = (i: number, value: string) => {
    onChange({ ...block, items: block.items.map((it, idx) => (idx === i ? value : it)) })
  }
  const remove = (i: number) => {
    onChange({ ...block, items: block.items.filter((_, idx) => idx !== i) })
  }
  const add = (value: string) => {
    onChange({ ...block, items: [...block.items, value] })
  }
  return (
    <div className="space-y-2">
      <SectionLabel value="Watch for" />
      <ul className="space-y-1.5">
        {block.items.map((it, i) => (
          <li key={i} className="group/item text-foreground/90 flex items-start gap-2 text-xs">
            <XIcon size={11} className="text-destructive mt-[3px] shrink-0" strokeWidth={2.5} />
            <InlineEditableTextarea
              value={it}
              onChange={(next) => update(i, next)}
              className="flex-1 leading-relaxed"
              placeholder="anti-pattern"
            />
            <button
              type="button"
              onClick={() => remove(i)}
              className="text-muted-foreground hover:text-destructive shrink-0 opacity-0 transition-opacity group-hover/item:opacity-100"
              aria-label="Remove item">
              <XIcon size={11} />
            </button>
          </li>
        ))}
      </ul>
      <AddItemInput onAdd={add} placeholder="don't use… + Enter" className="ml-5 text-xs" />
    </div>
  )
}

function NotesBlock({
  block,
  onChange,
}: {
  block: BlockOf<'notes'>
  onChange: (next: BlockOf<'notes'>) => void
}) {
  return (
    <div className="space-y-2">
      <SectionLabel value="Notes" />
      <InlineEditableTextarea
        value={block.text}
        onChange={(text) => onChange({ ...block, text })}
        className="text-muted-foreground text-[11px] leading-relaxed"
        placeholder="Meta-commentary, open questions"
      />
    </div>
  )
}

function ProseBlock({
  block,
  onChange,
}: {
  block: BlockOf<'prose'>
  onChange: (next: BlockOf<'prose'>) => void
}) {
  return (
    <div className="space-y-2">
      <SectionLabel value={block.label} onChange={(label) => onChange({ ...block, label })} />
      <InlineEditableTextarea
        value={block.content}
        onChange={(content) => onChange({ ...block, content })}
        className="text-foreground/90 text-xs leading-relaxed"
        placeholder="Click to edit"
      />
    </div>
  )
}

function BulletsBlock({
  block,
  onChange,
}: {
  block: BlockOf<'bullets'>
  onChange: (next: BlockOf<'bullets'>) => void
}) {
  const update = (i: number, value: string) => {
    onChange({ ...block, items: block.items.map((it, idx) => (idx === i ? value : it)) })
  }
  const remove = (i: number) => {
    onChange({ ...block, items: block.items.filter((_, idx) => idx !== i) })
  }
  const add = (value: string) => {
    onChange({ ...block, items: [...block.items, value] })
  }
  return (
    <div className="space-y-2">
      <SectionLabel value={block.label} onChange={(label) => onChange({ ...block, label })} />
      <ul className="list-disc space-y-1 pl-4">
        {block.items.map((it, i) => (
          <li key={i} className="group/item text-foreground/90 text-xs leading-relaxed">
            <span className="flex items-start gap-2">
              <InlineEditableTextarea
                value={it}
                onChange={(next) => update(i, next)}
                className="flex-1"
                placeholder="item"
              />
              <button
                type="button"
                onClick={() => remove(i)}
                className="text-muted-foreground hover:text-destructive shrink-0 opacity-0 transition-opacity group-hover/item:opacity-100"
                aria-label="Remove item">
                <XIcon size={11} />
              </button>
            </span>
          </li>
        ))}
      </ul>
      <AddItemInput onAdd={add} placeholder="item + Enter" className="ml-4 text-xs" />
    </div>
  )
}

// =========================================================================
// Add-block button — drops a new empty block of the chosen type at the end.
// =========================================================================

const BLOCK_TYPE_LABELS: Record<MoodboardBriefBlock['type'], string> = {
  throughline: 'Throughline',
  palette: 'Palette',
  audiences: 'Audiences',
  keyPhrases: 'Key phrases',
  watchFor: 'Watch for',
  notes: 'Notes',
  prose: 'Prose section',
  bullets: 'Bulleted list',
}

const EMPTY_BLOCKS: Record<MoodboardBriefBlock['type'], () => MoodboardBriefBlock> = {
  throughline: () => ({ type: 'throughline', text: '' }),
  palette: () => ({ type: 'palette', swatches: [{ hex: '#888888', name: null, role: null }] }),
  audiences: () => ({
    type: 'audiences',
    cards: [{ title: 'New audience', description: '' }],
  }),
  keyPhrases: () => ({ type: 'keyPhrases', phrases: [] }),
  watchFor: () => ({ type: 'watchFor', items: [] }),
  notes: () => ({ type: 'notes', text: '' }),
  prose: () => ({ type: 'prose', label: 'New section', content: '' }),
  bullets: () => ({ type: 'bullets', label: 'New list', items: [] }),
}

export function AddBlockButton({ onAdd }: { onAdd: (block: MoodboardBriefBlock) => void }) {
  const [open, setOpen] = useState(false)
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="text-muted-foreground gap-1.5">
          <PlusIcon size={14} />
          Add section
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-48 p-1" align="start">
        <div className="flex flex-col">
          {(Object.keys(EMPTY_BLOCKS) as Array<MoodboardBriefBlock['type']>).map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => {
                onAdd(EMPTY_BLOCKS[type]())
                setOpen(false)
              }}
              className="hover:bg-hover text-foreground cursor-pointer rounded-sm px-2 py-1.5 text-left text-xs">
              {BLOCK_TYPE_LABELS[type]}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}
